import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SharedModule } from 'src/shared/shared.module';
import {
  DisbursementRequestRevisionItem,
  DisbursementRevisionApprovableType,
  DisbursementRevisionCandidateRow,
  DisbursementRevisionTeachingSlot,
} from 'src/app/services/models/disbursement/disbursement.model';

declare const bootstrap: any;

export interface DisbursementRevisionDialogResult {
  reason: string;
  // Present only when the dialog opened with `showPicker` — an international-course
  // cert's reason-only send-back never sets these.
  items?: DisbursementRequestRevisionItem[];
  new_course_request_note?: string | null;
}

interface RevisionPickerRow {
  key: string;
  approvableType: DisbursementRevisionApprovableType;
  approvableId: number;
  subjectCode: string;
  subjectName: string;
  section: string;
  teachingType: string;
  teachingDays: string;
  teachingTimes: string;
  roomName: string;
  statusLabel: string;
  isSharedWorkload: boolean;
  selected: boolean;
  note: string;
  touched: boolean;
}

const STATIC_MODAL_OPTIONS = {
  backdrop: 'static',
  keyboard: false,
};
const CLOSE_FALLBACK_MS = 500;

const THAI_WEEKDAY_LABELS: Record<number, string> = {
  0: 'วันอาทิตย์',
  1: 'วันจันทร์',
  2: 'วันอังคาร',
  3: 'วันพุธ',
  4: 'วันพฤหัสบดี',
  5: 'วันศุกร์',
  6: 'วันเสาร์',
  7: 'วันอาทิตย์',
};

// Shared "ส่งกลับแก้ไข" dialog for the disbursement certification flow.
// ConfirmDialogComponent's own DESIGN.md forbids embedding form controls inside
// it, so this component clones its imperative open()/close() Bootstrap-modal
// lifecycle (static backdrop, stacked z-index teardown) while hosting a real
// reactive form.
//
// Two shapes share this one component:
// - reason-only (`showPicker` false, the default) — a single required reason
//   textarea. Used by the round-level (phase 2) send-back, and by an
//   international-course cert's phase-1 send-back (no single submission to
//   pick a course from).
// - picker (`showPicker` true, `candidates` populated from
//   `GET revision_candidates`) — a checkbox table of the sending cert's own
//   approved courses, a required per-row note once selected, and an optional
//   "ขอเพิ่มรายวิชาใหม่" note. The per-row/new-course notes are the reason in
//   this mode, cloned from `schedule-submissions-details.component.ts`'s
//   special-correction picker. A regular shared-workload row is still one
//   selectable item here; the API expands its selected assignment to the full
//   group and handles any affected cover-memo cancellation.
@Component({
  selector: 'app-disbursement-revision-dialog',
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
  templateUrl: './disbursement-revision-dialog.component.html',
  styleUrl: './disbursement-revision-dialog.component.scss',
})
export class DisbursementRevisionDialogComponent {
  @Input() modalId = 'disbursementRevisionDialog';
  @Input() title = 'ส่งกลับแก้ไข';
  // บรรทัดอธิบายใต้หัวข้อ — ใช้เมื่อหัวข้อบอกแค่ "ส่งกลับให้ใคร" แต่ยังต้องบอกว่าส่งกลับ
  // "เอกสารอะไร" (รอบส่งเบิก). ปล่อยเป็น null แล้วจะไม่เรนเดอร์เลย
  @Input() subtitle: string | null = null;
  @Input() cancelText = 'ยกเลิก';
  @Input() confirmText = 'ยืนยันส่งกลับแก้ไข';
  @Input() isProcessing = false;
  @Input() stackAboveModal = true;
  // Caller sets these directly on the ViewChild instance (see
  // disbursement-certification.component.ts's onOpenRevisionDialog) right before
  // calling open() — a template binding driven by a signal write in the same
  // synchronous call would not have flushed to this @Input yet.
  @Input() candidates: DisbursementRevisionCandidateRow[] = [];
  @Input() showPicker = false;

  @Output() submitted = new EventEmitter<DisbursementRevisionDialogResult>();
  @Output() cancelled = new EventEmitter<void>();

  form = inject(FormBuilder).group({
    reason: ['', Validators.required],
  });

  readonly pickerRows = signal<RevisionPickerRow[]>([]);
  readonly showNewCourseNote = signal(false);
  readonly newCourseNoteControl = new FormControl('', { nonNullable: true });
  readonly submitAttempted = signal(false);

  private readonly noteByKey = new Map<string, string>();
  private readonly touchedKeys = new Set<string>();

  open(): void {
    this.resetForm();

    const modalEl = document.getElementById(this.modalId);
    if (modalEl == null) {
      return;
    }

    bootstrap.Modal.getOrCreateInstance(modalEl, STATIC_MODAL_OPTIONS).show();
  }

  close(): void {
    const modalEl = document.getElementById(this.modalId);
    if (modalEl == null) {
      return;
    }

    this.blurFocusedElementInside(modalEl);

    const shouldRestoreModalOpen = this.stackAboveModal && this.hasAnotherOpenModal(modalEl);
    let completed = false;
    const complete = () => {
      if (completed) {
        return;
      }

      completed = true;
    };

    if (shouldRestoreModalOpen) {
      modalEl.addEventListener('hidden.bs.modal', () => {
        complete();
        queueMicrotask(() => {
          if (this.hasAnotherOpenModal(modalEl)) {
            document.body.classList.add('modal-open');
          }
        });
      }, { once: true });
    } else {
      modalEl.addEventListener('hidden.bs.modal', complete, { once: true });
    }

    bootstrap.Modal.getInstance(modalEl)?.hide();
    setTimeout(() => {
      if (completed) {
        return;
      }

      this.forceHideModalElement(modalEl);
      complete();
    }, CLOSE_FALLBACK_MS);
  }

  onCancel(): void {
    if (this.isProcessing) {
      return;
    }

    this.cancelled.emit();
    this.close();
  }

  onSubmit(): void {
    if (this.isProcessing) {
      return;
    }

    this.submitAttempted.set(true);

    if (this.form.invalid || !this.isPickerSelectionValid()) {
      this.form.markAllAsTouched();
      return;
    }

    const { reason } = this.form.getRawValue();
    const result: DisbursementRevisionDialogResult = { reason: String(reason ?? '').trim() };

    if (this.showPicker) {
      result.items = this.selectedPickerRows().map((row) => ({
        approvable_type: row.approvableType,
        approvable_id: row.approvableId,
        teacher_note: this.pickerRowNote(row).trim(),
      }));
      result.new_course_request_note = this.newCourseNoteControl.value.trim() || null;
    }

    this.submitted.emit(result);
  }

  // --- Picker (showPicker mode only) ---

  onShowNewCourseNote(): void {
    this.showNewCourseNote.set(true);
  }

  onTogglePickerRow(row: RevisionPickerRow, checked: boolean): void {
    this.pickerRows.update((rows) => rows.map((item) => (
      item.key === row.key
        ? { ...item, selected: checked, touched: item.touched || this.touchedKeys.has(item.key) || !checked }
        : item
    )));
  }

  onPickerRowNoteChange(row: RevisionPickerRow, event: Event): void {
    const value = (event.target as HTMLTextAreaElement | null)?.value ?? '';
    this.noteByKey.set(row.key, value);
    this.touchedKeys.add(row.key);
    row.note = value;
    row.touched = true;
  }

  pickerRowNote(row: RevisionPickerRow): string {
    return this.noteByKey.get(row.key) ?? row.note;
  }

  trackPickerRow(_index: number, row: RevisionPickerRow): string {
    return row.key;
  }

  selectedPickerRows(): RevisionPickerRow[] {
    return this.pickerRows().filter((row) => row.selected);
  }

  hasSharedWorkloadSelection(): boolean {
    return this.selectedPickerRows().some((row) => row.isSharedWorkload);
  }

  shouldShowPickerSelectionError(): boolean {
    return this.showPicker &&
      this.submitAttempted() &&
      this.selectedPickerRows().length === 0 &&
      this.newCourseNoteControl.value.trim() === '';
  }

  shouldShowPickerRowNoteError(row: RevisionPickerRow): boolean {
    return row.selected &&
      (row.touched || this.touchedKeys.has(row.key) || this.submitAttempted()) &&
      this.pickerRowNote(row).trim() === '';
  }

  private isPickerSelectionValid(): boolean {
    if (!this.showPicker) {
      return true;
    }

    const selected = this.selectedPickerRows();
    const hasNewCourseNote = this.newCourseNoteControl.value.trim() !== '';

    return (selected.length > 0 || hasNewCourseNote) &&
      selected.every((row) => this.pickerRowNote(row).trim() !== '');
  }

  private buildPickerRows(): RevisionPickerRow[] {
    return this.candidates.map((candidate) => ({
      key: `${candidate.approvable_type}:${candidate.approvable_id}`,
      approvableType: candidate.approvable_type,
      approvableId: candidate.approvable_id,
      subjectCode: this.displayValue(candidate.subject_code),
      subjectName: this.displayValue(candidate.subject_name),
      section: this.displayValue(candidate.section),
      teachingType: this.displayValue(candidate.teaching_type),
      teachingDays: this.formatTeachingDays(candidate.teaching_slots),
      teachingTimes: this.formatTeachingTimes(candidate.teaching_slots),
      roomName: this.formatRoomNames(candidate.teaching_slots),
      statusLabel: this.displayValue(candidate.status_label),
      isSharedWorkload: candidate.is_workload_split === true,
      selected: false,
      note: '',
      touched: false,
    }));
  }

  private formatTeachingDays(slots?: DisbursementRevisionTeachingSlot[] | null): string {
    return this.joinUnique((slots ?? []).map((slot) => THAI_WEEKDAY_LABELS[Number(slot.day_of_week)] ?? ''));
  }

  private formatTeachingTimes(slots?: DisbursementRevisionTeachingSlot[] | null): string {
    return this.joinUnique((slots ?? []).map((slot) => this.formatTimeRange(slot.start_time, slot.end_time)));
  }

  private formatRoomNames(slots?: DisbursementRevisionTeachingSlot[] | null): string {
    return this.joinUnique((slots ?? []).map((slot) => slot.room_name ?? ''));
  }

  private formatTimeRange(startTime?: string | null, endTime?: string | null): string {
    if (startTime == null || startTime === '' || endTime == null || endTime === '') {
      return '';
    }

    return `${startTime} - ${endTime} น.`;
  }

  private joinUnique(values: string[]): string {
    const uniqueValues = values
      .map((value) => value.trim())
      .filter((value, index, items) => value !== '' && items.indexOf(value) === index);

    return uniqueValues.length > 0 ? uniqueValues.join(' / ') : '-';
  }

  private displayValue(value: string | number | null | undefined): string {
    return value == null || value === '' ? '-' : String(value);
  }

  private resetForm(): void {
    this.form.reset({ reason: '' });
    this.noteByKey.clear();
    this.touchedKeys.clear();
    this.submitAttempted.set(false);
    this.showNewCourseNote.set(false);
    this.newCourseNoteControl.setValue('');
    this.pickerRows.set(this.buildPickerRows());
    this.syncReasonValidators();
  }

  // Reason is collected by the dedicated textarea only in reason-only mode.
  // Picker mode sends per-row/new-course notes and the backend synthesizes the
  // correction request reason from those values.
  private syncReasonValidators(): void {
    const control = this.form.controls.reason;

    if (this.showPicker) {
      control.clearValidators();
    } else {
      control.setValidators(Validators.required);
    }

    control.updateValueAndValidity();
  }

  private hasAnotherOpenModal(modalEl: HTMLElement): boolean {
    return Array.from(document.querySelectorAll<HTMLElement>('.modal.show'))
      .some((openModal) => openModal !== modalEl);
  }

  private blurFocusedElementInside(modalEl: HTMLElement): void {
    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && modalEl.contains(activeElement)) {
      activeElement.blur();
    }
  }

  private forceHideModalElement(modalEl: HTMLElement): void {
    modalEl.classList.remove('show');
    modalEl.setAttribute('aria-hidden', 'true');
    modalEl.removeAttribute('aria-modal');
    modalEl.removeAttribute('role');
    modalEl.style.display = 'none';

    if (this.hasAnotherOpenModal(modalEl)) {
      document.body.classList.add('modal-open');
      return;
    }

    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
  }
}
